import React, { useState, useEffect } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import axios from "axios";
import { colors } from "../theme";
import dayjs from "dayjs";
import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";

// Material UI components
import {
  Box,
  Typography,
  TextField,
  Button,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  ListItemSecondaryAction,
  Checkbox,
  IconButton,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControlLabel,
  Grid,
  Divider,
  Snackbar,
  Alert,
  CircularProgress,
  InputAdornment,
  Tooltip,
} from "@mui/material";

// Material UI icons
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import EventIcon from "@mui/icons-material/Event";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";

// Determine the API URL based on environment
const API_URL =
  process.env.NODE_ENV === "production"
    ? window.location.origin
    : "http://localhost:8080";

const Reminders = () => {
  // Auth state
  const { getAccessTokenSilently, isAuthenticated, loginWithRedirect } =
    useAuth0();

  // Reminders state
  const [reminders, setReminders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // UI state
  const [openDialog, setOpenDialog] = useState(false);
  const [editingReminder, setEditingReminder] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  // Form state
  const [title, setTitle] = useState("");
  const [description, setDescription] = useState("");
  const [dueDate, setDueDate] = useState(null);
  const [isCompleted, setIsCompleted] = useState(false);

  // Fetch reminders on component mount
  useEffect(() => {
    if (isAuthenticated) {
      fetchReminders();
    }
  }, [isAuthenticated]);

  // Fetch reminders from the API
  const fetchReminders = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = await getAccessTokenSilently();
      const response = await axios.get(`${API_URL}/api/reminders`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      setReminders(response.data);
    } catch (err) {
      console.error("Error fetching reminders:", err);
      setError("Failed to load reminders. Please try again later.");
    } finally {
      setLoading(false);
    }
  };

  // Handle creating/updating a reminder
  const handleSaveReminder = async () => {
    if (!title.trim()) {
      setSnackbar({
        open: true,
        message: "Title is required",
        severity: "error",
      });
      return;
    }

    try {
      const token = await getAccessTokenSilently();
      const reminderData = {
        title,
        description,
        dueDate: dueDate ? dueDate.toISOString() : undefined,
        isCompleted,
      };

      if (editingReminder) {
        // Update existing reminder
        await axios.put(
          `${API_URL}/api/reminders/${editingReminder._id}`,
          reminderData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );

        setSnackbar({
          open: true,
          message: "Reminder updated successfully!",
          severity: "success",
        });
      } else {
        // Create new reminder
        await axios.post(`${API_URL}/api/reminders`, reminderData, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setSnackbar({
          open: true,
          message: "Reminder created successfully!",
          severity: "success",
        });
      }

      // Refresh the list and close the dialog
      await fetchReminders();
      handleCloseDialog();
    } catch (err) {
      console.error("Error saving reminder:", err);
      setSnackbar({
        open: true,
        message: "Failed to save reminder. Please try again.",
        severity: "error",
      });
    }
  };

  // Handle deleting a reminder
  const handleDeleteReminder = async (id) => {
    try {
      const token = await getAccessTokenSilently();
      await axios.delete(`${API_URL}/api/reminders/${id}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      // Refresh the list
      await fetchReminders();
      setSnackbar({
        open: true,
        message: "Reminder deleted successfully!",
        severity: "success",
      });
    } catch (err) {
      console.error("Error deleting reminder:", err);
      setSnackbar({
        open: true,
        message: "Failed to delete reminder. Please try again.",
        severity: "error",
      });
    }
  };

  // Handle toggling a reminder's completion status
  const handleToggleCompletion = async (id) => {
    try {
      const token = await getAccessTokenSilently();
      await axios.patch(
        `${API_URL}/api/reminders/${id}/toggle`,
        {},
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      // Refresh the list
      await fetchReminders();
    } catch (err) {
      console.error("Error toggling reminder:", err);
      setSnackbar({
        open: true,
        message: "Failed to update reminder status. Please try again.",
        severity: "error",
      });
    }
  };

  // Handle opening the dialog for creating a new reminder
  const handleOpenCreateDialog = () => {
    setEditingReminder(null);
    setTitle("");
    setDescription("");
    setDueDate(null);
    setIsCompleted(false);
    setOpenDialog(true);
  };

  // Handle opening the dialog for editing an existing reminder
  const handleOpenEditDialog = (reminder) => {
    setEditingReminder(reminder);
    setTitle(reminder.title);
    setDescription(reminder.description || "");
    setDueDate(reminder.dueDate ? dayjs(reminder.dueDate) : null);
    setIsCompleted(reminder.isCompleted);
    setOpenDialog(true);
  };

  // Handle closing the dialog
  const handleCloseDialog = () => {
    setOpenDialog(false);
    setEditingReminder(null);
  };

  // Handle closing the snackbar
  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  // Format the due date for display
  const formatDueDate = (date) => {
    if (!date) return "No due date";
    const options = {
      weekday: "long",
      year: "numeric",
      month: "long",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(date).toLocaleDateString(undefined, options);
  };

  // If not authenticated, redirect to login
  if (!isAuthenticated) {
    return (
      <Box sx={{ textAlign: "center", mt: 5 }}>
        <Typography variant="h5" gutterBottom>
          Please log in to access your reminders
        </Typography>
        <Button
          variant="contained"
          color="primary"
          onClick={() => loginWithRedirect()}
          sx={{ mt: 2 }}
        >
          Log In
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ maxWidth: 800, mx: "auto", px: 2, pb: 4 }}>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
          mb: 3,
        }}
      >
        <Typography variant="h4" component="h1" sx={{ fontWeight: "bold" }}>
          Reminders
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleOpenCreateDialog}
          sx={{
            backgroundColor: colors.primary,
            "&:hover": {
              backgroundColor: colors.primaryDark,
            },
          }}
        >
          Add Reminder
        </Button>
      </Box>

      <Paper sx={{ borderRadius: 2, overflow: "hidden" }}>
        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <Box sx={{ p: 3, textAlign: "center" }}>
            <Typography color="error">{error}</Typography>
            <Button variant="outlined" sx={{ mt: 2 }} onClick={fetchReminders}>
              Try Again
            </Button>
          </Box>
        ) : reminders.length === 0 ? (
          <Box sx={{ p: 4, textAlign: "center" }}>
            <Typography variant="body1" sx={{ mb: 2 }}>
              You don't have any reminders yet.
            </Typography>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleOpenCreateDialog}
              sx={{
                backgroundColor: colors.primary,
                "&:hover": {
                  backgroundColor: colors.primaryDark,
                },
              }}
            >
              Create Your First Reminder
            </Button>
          </Box>
        ) : (
          <List>
            {reminders.map((reminder, index) => (
              <React.Fragment key={reminder._id}>
                {index > 0 && <Divider component="li" />}
                <ListItem
                  sx={{
                    backgroundColor: reminder.isCompleted
                      ? `${colors.success}10`
                      : "inherit",
                    transition: "background-color 0.3s",
                  }}
                >
                  <ListItemIcon>
                    <Checkbox
                      edge="start"
                      checked={reminder.isCompleted}
                      onChange={() => handleToggleCompletion(reminder._id)}
                      icon={<RadioButtonUncheckedIcon />}
                      checkedIcon={
                        <CheckCircleIcon sx={{ color: colors.success }} />
                      }
                    />
                  </ListItemIcon>
                  <ListItemText
                    primary={
                      <Typography
                        variant="body1"
                        sx={{
                          textDecoration: reminder.isCompleted
                            ? "line-through"
                            : "none",
                          color: reminder.isCompleted
                            ? "text.secondary"
                            : "text.primary",
                          fontWeight: reminder.isCompleted
                            ? "normal"
                            : "medium",
                        }}
                      >
                        {reminder.title}
                      </Typography>
                    }
                    secondary={
                      <>
                        {reminder.description && (
                          <Typography
                            variant="body2"
                            color="text.secondary"
                            sx={{
                              display: "-webkit-box",
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: "vertical",
                              overflow: "hidden",
                              mb: 0.5,
                            }}
                          >
                            {reminder.description}
                          </Typography>
                        )}
                        {reminder.dueDate && (
                          <Box sx={{ display: "flex", alignItems: "center" }}>
                            <EventIcon
                              fontSize="small"
                              sx={{ mr: 0.5, fontSize: 16 }}
                            />
                            <Typography variant="caption">
                              {formatDueDate(reminder.dueDate)}
                            </Typography>
                          </Box>
                        )}
                      </>
                    }
                  />
                  <ListItemSecondaryAction>
                    <Tooltip title="Edit">
                      <IconButton
                        edge="end"
                        onClick={() => handleOpenEditDialog(reminder)}
                        sx={{ mr: 1 }}
                      >
                        <EditIcon />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete">
                      <IconButton
                        edge="end"
                        onClick={() => handleDeleteReminder(reminder._id)}
                      >
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  </ListItemSecondaryAction>
                </ListItem>
              </React.Fragment>
            ))}
          </List>
        )}
      </Paper>

      {/* Create/Edit Reminder Dialog */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {editingReminder ? "Edit Reminder" : "Create New Reminder"}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 0 }}>
            <Grid item xs={12}>
              <TextField
                autoFocus
                label="Title"
                fullWidth
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                variant="outlined"
                margin="normal"
                required
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Description"
                fullWidth
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                variant="outlined"
                margin="normal"
                multiline
                rows={3}
              />
            </Grid>
            <Grid item xs={12}>
              <DateTimePicker
                label="Due Date and Time"
                value={dueDate}
                onChange={(newValue) => setDueDate(newValue)}
                slotProps={{
                  textField: {
                    variant: "outlined",
                    fullWidth: true,
                    margin: "normal",
                    InputProps: {
                      startAdornment: (
                        <InputAdornment position="start">
                          <EventIcon />
                        </InputAdornment>
                      ),
                    },
                  },
                }}
              />
            </Grid>
            {editingReminder && (
              <Grid item xs={12}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={isCompleted}
                      onChange={(e) => setIsCompleted(e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Mark as completed"
                />
              </Grid>
            )}
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Cancel</Button>
          <Button
            onClick={handleSaveReminder}
            variant="contained"
            sx={{
              backgroundColor: colors.primary,
              "&:hover": {
                backgroundColor: colors.primaryDark,
              },
            }}
          >
            {editingReminder ? "Update" : "Create"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Reminders;
